import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Newspaper, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

function fmtDate(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function News() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [news, setNews] = useState([]);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ title: "", category: "Announcement", body: "", date: today });

  const load = () => api.get("/news").then((r) => setNews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    try { await api.post("/news", { ...form, author: user.name }); toast.success("Posted"); setOpen(false); setForm({ title: "", category: "Announcement", body: "", date: today }); load(); }
    catch { toast.error("Could not post"); }
  };

  return (
    <div data-testid="news-page">
      <PageHeader overline="Firm Communications" title="Firm News & Announcements" subtitle="Live updates, wins, practice alerts and firm-wide announcements.">
        {editor && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-news-btn"><Plus className="h-4 w-4 mr-1.5" /> New Post</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Title</Label><Input data-testid="news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Category</Label><Input data-testid="news-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                  <div><Label className="text-xs">Date</Label><Input type="date" data-testid="news-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                </div>
                <div><Label className="text-xs">Body</Label><Textarea data-testid="news-body" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="save-news-btn" onClick={save}>Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {news.length === 0 ? <EmptyState icon={Newspaper} title="No news yet" /> : (
        <div className="space-y-4" data-testid="news-list">
          {news.map((n) => (
            <Link key={n.id} to={`/news/${n.id}`} data-testid="news-item" className="block border border-border bg-card rounded-sm p-6 hover:border-primary transition-colors animate-fade-up">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 bg-primary/10 text-primary rounded-sm">{n.category}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(n.date)} · {n.author}</span>
              </div>
              <h2 className="font-display font-bold text-xl">{n.title}</h2>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{n.body}</p>
              <span className="inline-flex items-center gap-1 text-sm text-primary mt-3">Read more <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
